// Guessing which CSV column a school meant, so the operator does not have to
// map it by hand.
//
// The rules here were derived from what schools have ACTUALLY sent us, not from
// imagination: 43,947 header choices stored in `student_imports.header_mapping`
// were replayed through candidate matchers. The old rule -- raw, case-sensitive
// `includes` against the field key and its label -- auto-matched 66.0% of them.
// This one matches 97.9%.
//
// What was costing operators their time:
//
//   1. CASE AND SEPARATORS. `GRADE`, `LastName`, `First_Name`, `Grade_Level`.
//   2. GENUINE SYNONYMS. `fname`, `lname`, `Perm ID`, `Gr`, `advisorFullName`,
//      `E-Mail`, `Home Telephone` -- these need the alias table below.
//
// NOT byte-order marks, though the stored mappings are full of them
// (`ï»¿Student ID` 1,535 times). ReadCsv strips the BOM at the door since #5042
// (2026-08-19), so a header never reaches this code carrying one. And it was
// never the cause anyway: replaying the archive with the BOM pre-stripped moves
// the old matcher by 15 rows out of 43,954, because those headers (`ï»¿ID`,
// `ï»¿StudentID`) fail on case and format too. Normalising handles a BOM if one
// ever slips through, but that is belt-and-braces, not the win.
//
// Biggest wins: teacher 22.2% -> 96.8%, email 38.8% -> 99.2%, student_id
// 60.1% -> 95.6%.

// The operator-facing name of every field the importer maps, and the ONE place
// they are defined. The header step shows these beside each Select and the
// preview's column check names fields with them; when the two screens derived
// their own labels independently the preview said "Student Id" where the header
// step said "Student ID", and the operator has no way to know those are the
// same field.
export const FIELD_LABELS = {
  student_id: 'Student ID',
  first_name: 'First Name',
  last_name: 'Last Name',
  grade: 'Grade',
  teacher: 'Teacher Name',
  email: 'Parent Email',
  phone: 'Phone',
  period: 'Period',
  mailing_address: 'Mailing Address',
  mailing_address2: 'Mailing Address 2',
  mailing_zip: 'ZIP Code',
  mailing_city: 'Mailing City',
  mailing_state: 'Mailing State',
  position: 'Position',
};

// The label for a field key. Falls back to title-casing the key so a field
// added server-side still reads sensibly before it is listed above.
export const fieldLabel = (field) => FIELD_LABELS[field] || String(field || '')
  .split('_')
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

// Lowercase, and drop everything that is not a letter or a digit. That single
// step removes the BOM, the case variants and the separator variants at once.
export const normalizeHeader = (value) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

// CURATED from the real distribution, not derived from it wholesale. Three
// mappings in the data are deliberately NOT here:
//
//   `Group` -> grade (22x) is ambiguous -- a group column is not a grade.
//   `ST`    -> grade (15x) is the staff grade code, not a header name.
//   `Barcode` -> phone (9x) is an operator mistake.
//
// Automating those would turn a human error into a systematic one. They stay
// manual, which is the right answer for a genuinely ambiguous column.
export const HEADER_ALIASES = {
  student_id: ['id', 'permid', 'studentnumber', 'studentno', 'idnumber', 'studentstudentnumber',
    'sisid', 'localid', 'stuid', 'childid', 'ident', 'stustuid', 'personid'],
  first_name: ['first', 'fname', 'stfname', 'fn', 'studentfirstname', 'studentfirst', 'givenname'],
  last_name: ['last', 'lname', 'stlname', 'ln', 'studentlastname', 'studentlast', 'surname',
    'familyname'],
  grade: ['gr', 'grd', 'gradelevel', 'gradelvl', 'studentgrade'],
  teacher: ['advisorfullname', 'advisor', 'advisorlastname', 'tchrlastname', 'tchr', 'tchrname',
    'staffname', 'homeroom', 'homeroomteacher', 'primaryteachername', 'counselor', 'teachername'],
  email: ['parentemail', 'guardianemail', 'contactemail', 'emailaddress', 'emailaddr'],
  phone: ['parentphone', 'guardianphone', 'homephone', 'cellphone', 'mobile', 'telephone',
    'primaryphone', 'householdphone'],
  mailing_address: ['address', 'address1', 'street', 'streetaddress', 'mailingaddress1'],
  mailing_address2: ['address2', 'apt', 'unit', 'suite'],
  mailing_city: ['city', 'town'],
  mailing_state: ['state', 'province', 'region'],
  mailing_zip: ['zip', 'zipcode', 'postal', 'postalcode'],
};

// A header that is three characters or fewer is only accepted as an EXACT
// normalised match or a listed alias. Without that floor, `t.includes(h)` lets
// a stray one-letter column match half the table.
const SHORT_HEADER_LENGTH = 3;

// A field whose name ends in a digit -- mailing_address2 -- is a SIBLING of the
// unnumbered one, and its normalised name CONTAINS the sibling's:
// 'mailingaddress2'.includes('address') is true. So the reverse-direction rule
// would let a single bare `Address` column default both Selects to itself, and
// line 1 of the address would import as line 2. Numbered fields take an exact
// or alias match only.
const isNumberedField = (key) => /\d$/.test(String(key));

// Does `header` name the field `key` (whose human label is `label`)?
export const headerMatchesField = (header, key, label) => {
  const h = normalizeHeader(header);
  if (!h) return false;

  const targets = [normalizeHeader(key), normalizeHeader(label)].filter(Boolean);

  // The header contains the field name: "Student First Name" -> first_name.
  if (targets.some((t) => h === t || h.includes(t))) return true;

  // ...and the other direction: a bare "Address" -> mailing_address. Guarded on
  // length, which is why "ID" is an alias rather than relying on this, and
  // skipped for numbered siblings -- see isNumberedField.
  if (!isNumberedField(key) && h.length > SHORT_HEADER_LENGTH
      && targets.some((t) => t.includes(h))) return true;

  return (HEADER_ALIASES[key] || []).includes(h);
};

// The first header in the file that names this field, in the shape the Select
// wants, or undefined when nothing does -- the operator maps it by hand.
//
// FIRST, not best: the file's own column order is the only signal available for
// a tie, and a school that repeats a name ("Last Name", "Last Name1") means the
// first one.
export const findHeaderMatch = (headers, key, label) => {
  const match = (headers || []).find((header) => headerMatchesField(header, key, label));

  return match ? { label: match, value: match } : undefined;
};
